/**
 * Normalizes artist name based on business rules
 * @param {string} artistName - The original artist name
 * @param {string} title - The original title
 * @returns {string} - The normalized artist name
 */
export function normalizeArtistName(artistName, title) {
  if (!artistName) return artistName;

  // If name includes 'KidSuper', normalize to exactly 'KidSuper'
  if (artistName.includes("KidSuper")) {
    return "KidSuper";
  }
  if (artistName.includes("Tanguy")) {
    return "Tanguy Bourhis";
  }
  if (title.includes("Australian GP 2026")) {
    return "Elizabeth Langreiter";
  }
  if (title.includes("Chinese GP 2026")) {
    return "Jobey Hon";
  }
  if (title.includes("Japanese GP 2026")) {
    return "Nori Okawa";
  }
  if (title.includes("Miami GP 2026")) {
    return "Dimithry Victor";
  }
  if (title.includes("Canadian GP 2026")) {
    return "Owen Rival";
  }
  if (title.includes("Premier League")) {
    return "Reuben Dangoor";
  }
  if (title.includes("Monaco GP 2026")) {
    return "Cesar Botta";
  }
  return artistName;
}

/**
 * Processes variant title for special cases
 * @param {string} title - The certificate title
 * @param {string} variantTitle - The original variant title
 * @returns {string} - The processed variant title
 */
export function processVariantTitle(title, variantTitle) {
  if (!title || !variantTitle) return variantTitle;

  if (title.toLowerCase() === "this lime green and pleasant land") {
    let dimensions = variantTitle;
    if (dimensions.includes("Small")) {
      dimensions = "Small (20 x 20 inches)";
    } else if (dimensions.includes("Medium")) {
      dimensions = "Medium (24 x 24 inches)";
    } else if (dimensions.includes("Large")) {
      dimensions = "Large (30 x 30 inches)";
    }
    return dimensions;
  }

  return variantTitle;
}

/**
 * Formats a date for certificate display
 * @param {string|Date} printDate - The print date
 * @returns {string} - Formatted date string
 */
export function formatPrintDate(printDate) {
  const date = new Date(printDate);
  const options = { month: "short", day: "numeric", year: "numeric" };
  return date.toLocaleDateString("en-US", options);
}

/**
 * Generates product title URL slug
 * @param {string} title - The product title
 * @returns {string} - URL-friendly slug
 */
export function getProductTitleForUrl(title) {
  return title.toLowerCase().replaceAll(" ", "-");
}

/**
 * Generates QR code data URL
 * @param {string} productTitleForUrl - URL slug for the product
 * @returns {string} - QR code image URL
 */
export function getQrUrl(productTitleForUrl) {
  const qrData = `https://oditi.com/products/${encodeURIComponent(productTitleForUrl)}`;
  return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}&color=111111%color=F4F4F4`;
}

/**
 * Processes certificate data and returns all derived values
 * This centralizes all certificate logic to ensure consistency across components
 * @param {Object} cert - The certificate object
 * @param {string|Date} printDate - The print date
 * @returns {Object} - Processed certificate data with all derived values
 */
export function processCertificateData(cert, printDate) {
  // Normalize artist name
  const normalizedArtist = normalizeArtistName(cert.artist, cert.title);

  // Process variant title
  const processedVariantTitle = processVariantTitle(cert.title, cert.variant_title);

  // Create a new cert object with processed values (don't mutate original)
  const processedCert = {
    ...cert,
    artist: normalizedArtist,
    variant_title: processedVariantTitle,
  };

  // Generate derived values
  const formattedPrintDate = formatPrintDate(printDate);
  const productTitleForUrl = getProductTitleForUrl(cert.title);
  const qrUrl = getQrUrl(productTitleForUrl);

  return {
    cert: processedCert,
    formattedPrintDate,
    productTitleForUrl,
    qrUrl,
  };
}
